/**
 * AssetLedger Pro — API Hooks (React Query)
 *
 * Centralises all data fetching with:
 * - Automatic caching and background refetch
 * - Optimistic updates for mutations
 * - Loading / error states
 * - Automatic invalidation after mutations
 *
 * Pattern: one file per domain (assets, users, settings, etc.)
 */

import {
  useQuery, useMutation, useQueryClient,
  UseQueryOptions, keepPreviousData,
} from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  Asset, AssetListItem, AssetCategory, AssetBlock, User,
  ClientSettings, FinancialYear, DepreciationRun, Alert,
  DashboardStats, Branch, InsurancePolicy, AMCContract,
  PaginationMeta, AssetDisposal, AssetBatch,
} from '@/types';

// ── Query Key Factory ─────────────────────────────────────────────────────────
// Centralised keys ensure consistent cache invalidation

export const QK = {
  dashboard:         ['dashboard'] as const,
  assets:            (params?: object) => ['assets', params] as const,
  asset:             (id: string) => ['asset', id] as const,
  assetBatches:      ['asset-batches'] as const,
  categories:        ['categories'] as const,
  category:          (id: string) => ['category', id] as const,
  blocks:            ['blocks'] as const,
  block:             (id: string) => ['block', id] as const,
  users:             (params?: object) => ['users', params] as const,
  user:              (id: string) => ['user', id] as const,
  settings:          ['settings'] as const,
  financialYears:    ['financial-years'] as const,
  branches:          ['branches'] as const,
  depreciationRuns:  (params?: object) => ['depreciation-runs', params] as const,
  disposals:         (params?: object) => ['disposals', params] as const,
  alerts:            ['alerts'] as const,
  insurance:         (assetId: string) => ['insurance', assetId] as const,
  amc:               (assetId: string) => ['amc', assetId] as const,
  auditLogs:         (params?: object) => ['audit-logs', params] as const,
  verificationSessions: ['verification-sessions'] as const,
};

// ── Dashboard ─────────────────────────────────────────────────────────────────

export function useDashboardStats(financialYearId?: string) {
  return useQuery({
    queryKey: [...QK.dashboard, financialYearId],
    queryFn: async () => {
      const r = await api.get<{ data: DashboardStats }>('/dashboard/stats', {
        params: financialYearId ? { financialYearId } : undefined,
      });
      return r.data.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// ── Block of Assets — the year's movement ─────────────────────────────────────

export interface BlockStatementRow {
  blockId: string;
  blockCode: string;
  blockName: string;
  depreciationRate: number;
  openingWDV: number;
  additions: number;
  disposals: number;
  subsidy: number;
  depreciation: number;
  additionalDepreciation: number;
  closingWDV: number;
  assetCount: number;
}

export interface BlockStatement {
  financialYear: { id: string; label: string } | null;
  /** AS_FILED — the client's own figures for a migrated year. COMPUTED — this engine's. */
  basis: 'AS_FILED' | 'COMPUTED' | null;
  /** False when the year has not been run: its depreciation and closing are not
   *  nil, they are not yet known, and must not be printed as zero. */
  hasPostedRun: boolean;
  rows: BlockStatementRow[];
  /** Assets in no block, or in one carrying no rate — not blocks of assets under
   *  s.2(11), so excluded from the blocks and totals, but disclosed. */
  nonDepreciable: Array<{ name: string; value: number }>;
  totals: {
    openingWDV: number; additions: number; disposals: number;
    subsidy: number; depreciation: number; closingWDV: number;
  } | null;
}

/**
 * The year's movement per income tax block.
 *
 * Not derived from `AssetBlock.openingWDV`, which is only written when a
 * financial year is CLOSED — and MIPL's years are deliberately never closed,
 * because closing one rolls balances forward and would stamp today's written-down
 * value onto a historic year. That field is nil for every block and always will
 * be, which is why the page could not show opening or depreciation before.
 */
export function useBlockStatement(financialYearId?: string) {
  return useQuery({
    queryKey: ['blocks', 'statement', financialYearId],
    queryFn: async () => {
      const r = await api.get<{ data: BlockStatement }>('/blocks/statement', {
        params: financialYearId ? { financialYearId } : undefined,
      });
      return r.data.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

// ── Assets ────────────────────────────────────────────────────────────────────

export interface AssetFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  categoryId?: string;
  blockId?: string;
  branchId?: string;
  departmentId?: string;
  batchId?: string;
  /** Restates the register for a chosen financial year — see GET /assets. */
  financialYearId?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface AssetListResponse {
  items: AssetListItem[];
  meta: PaginationMeta;
}

export function useAssets(filters: AssetFilters = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: QK.assets(filters),
    queryFn: async () => {
      const r = await api.get<{ data: AssetListItem[]; meta: PaginationMeta }>('/assets', {
        params: filters,
      });
      return { items: r.data.data, meta: r.data.meta! };
    },
    placeholderData: keepPreviousData,
    enabled: options.enabled,
  });
}

export function useAsset(id: string) {
  return useQuery({
    queryKey: QK.asset(id),
    queryFn: async () => {
      const r = await api.get<{ data: Asset }>(`/assets/${id}`);
      return r.data.data;
    },
    enabled: !!id,
  });
}

export function useCreateAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<Asset>) => {
      const r = await api.post<{ data: Asset }>('/assets', data);
      return r.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets'] });
      qc.invalidateQueries({ queryKey: QK.dashboard });
    },
  });
}

/** Same POST /assets endpoint, used when quantity > 1 — the response shape
 * differs (a batch header + the created units) so callers need the right
 * type instead of squinting at useCreateAsset's single-Asset return. */
export function useCreateAssetBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const r = await api.post<{ data: { id: string; batchId: string; quantity: number; assets: Asset[] } }>('/assets', data);
      return r.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets'] });
      qc.invalidateQueries({ queryKey: QK.assetBatches });
      qc.invalidateQueries({ queryKey: QK.dashboard });
    },
  });
}

export interface AssetBatchSummary extends AssetBatch {
  currentUnitCount: number;
  statusCounts: Record<string, number>;
}

export function useAssetBatches() {
  return useQuery({
    queryKey: QK.assetBatches,
    queryFn: async () => {
      const r = await api.get<{ data: AssetBatchSummary[] }>('/assets/batches');
      return r.data.data;
    },
  });
}

export function useDisposeBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      disposals: { assetId: string; saleProceeds: number }[];
      disposalType: string;
      disposalDate: string;
      buyerName?: string;
      buyerGSTIN?: string;
      invoiceNumber?: string;
      remarks?: string;
      gstRatePercent?: number;
    }) => {
      const r = await api.post('/assets/dispose-batch', data);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets'] });
      qc.invalidateQueries({ queryKey: QK.assetBatches });
      qc.invalidateQueries({ queryKey: ['blocks'] });
      qc.invalidateQueries({ queryKey: QK.dashboard });
    },
  });
}

/** Retroactively split an existing asset row into N units — upload phase 1. */
export function useSplitValidate() {
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const r = await api.post('/assets/split/validate', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return r.data.data;
    },
  });
}

/** Retroactive asset split — upload phase 2, applies the validated rows. */
export function useSplitConfirm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { rows: { assetCode: string; quantity: number }[] }) => {
      const r = await api.post('/assets/split/confirm', data);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets'] });
      qc.invalidateQueries({ queryKey: QK.assetBatches });
      qc.invalidateQueries({ queryKey: ['blocks'] });
      qc.invalidateQueries({ queryKey: QK.dashboard });
    },
  });
}

/** Government subsidy (CCIS/PLI) WDV correction — upload phase 1: preview. */
export function useSubsidyValidate() {
  return useMutation({
    mutationFn: async ({ file, financialYearId }: { file: File; financialYearId: string }) => {
      const formData = new FormData();
      formData.append('file', file);
      const r = await api.post(`/assets/subsidies/validate?financialYearId=${financialYearId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return r.data.data;
    },
  });
}

/** Subsidy correction — upload phase 2, creates + applies the correction. */
export function useSubsidyConfirm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      financialYearId: string;
      schemeName: string;
      referenceNumber?: string;
      dateReceived: string;
      notes?: string;
      allocations: { assetCode: string; amount: number }[];
    }) => {
      const r = await api.post('/assets/subsidies/confirm', data);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets'] });
      qc.invalidateQueries({ queryKey: ['blocks'] });
      qc.invalidateQueries({ queryKey: QK.dashboard });
      qc.invalidateQueries({ queryKey: ['subsidies'] });
    },
  });
}

export function useSubsidies() {
  return useQuery({
    queryKey: ['subsidies'],
    queryFn: async () => {
      const r = await api.get('/assets/subsidies');
      return r.data.data;
    },
  });
}

/** Additional depreciation (Sec 32(1)(iia)) retroactive recompute — fast, approximate estimate. */
export function useAdditionalDepRecomputePreview() {
  return useMutation({
    mutationFn: async () => {
      const r = await api.post('/assets/additional-depreciation/preview', {});
      return r.data.data;
    },
  });
}

/** Queues the full-history recompute as a background job (or an approval request first). */
export function useAdditionalDepRecomputeConfirm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const r = await api.post('/assets/additional-depreciation/confirm', {});
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['additional-depreciation-recomputes'] });
      qc.invalidateQueries({ queryKey: ['background-jobs'] });
    },
  });
}

export function useAdditionalDepRecomputes() {
  return useQuery({
    queryKey: ['additional-depreciation-recomputes'],
    queryFn: async () => {
      const r = await api.get('/assets/additional-depreciation');
      return r.data.data;
    },
  });
}

export function useUpdateAsset(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<Asset>) => {
      const r = await api.put<{ data: Asset }>(`/assets/${id}`, data);
      return r.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.asset(id) });
      qc.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

/// Soft-delete an asset (moves it to Deleted Assets). Block WDV is adjusted
/// server-side. Reversible via restore.
export function useDeleteAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await api.delete(`/assets/${id}`);
      return r.data;
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: QK.asset(id) });
      qc.invalidateQueries({ queryKey: ['assets'] });
      qc.invalidateQueries({ queryKey: ['assets-deleted'] });
      qc.invalidateQueries({ queryKey: ['blocks'] });
      qc.invalidateQueries({ queryKey: QK.dashboard });
    },
  });
}

/// Paginated list of soft-deleted assets for the "Deleted Assets" page.
export interface DeletedAsset {
  id: string;
  assetCode: string;
  name: string;
  status: string;
  totalCapitalisedCost: number;
  caCurrentWDV: number;
  itCurrentWDV: number;
  deletedAt: string;
  deletedById: string | null;
  deletedBy: { id: string; name: string; email: string } | null;
  category?: { name: string; code: string } | null;
  block?: { blockCode: string; blockName: string } | null;
  canPermanentlyDelete: boolean;
}

export function useDeletedAssets(params: { page?: number; search?: string } = {}) {
  return useQuery({
    queryKey: ['assets-deleted', params],
    queryFn: async () => {
      const r = await api.get<{ data: DeletedAsset[]; meta: PaginationMeta }>(
        '/assets/deleted', { params }
      );
      return { items: r.data.data, meta: r.data.meta! };
    },
    placeholderData: keepPreviousData,
  });
}

export function useRestoreAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await api.post(`/assets/${id}/restore`);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets'] });
      qc.invalidateQueries({ queryKey: ['assets-deleted'] });
      qc.invalidateQueries({ queryKey: ['blocks'] });
      qc.invalidateQueries({ queryKey: QK.dashboard });
    },
  });
}

/// Permanently remove a soft-deleted asset. CLIENT_ADMIN only; server refuses
/// for assets with any depreciation, disposal, or transfer history.
export function usePermanentDeleteAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await api.delete(`/assets/${id}/permanent`);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets-deleted'] });
    },
  });
}

export function useDisposeAsset(assetId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      disposalType: string;
      disposalDate: string;
      saleProceeds: number;
      buyerName?: string;
      invoiceNumber?: string;
      remarks?: string;
    }) => {
      const r = await api.post(`/assets/${assetId}/dispose`, data);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.asset(assetId) });
      qc.invalidateQueries({ queryKey: ['assets'] });
      qc.invalidateQueries({ queryKey: ['blocks'] });
      qc.invalidateQueries({ queryKey: QK.dashboard });
    },
  });
}

export function useTransferAsset(assetId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      toBranchId?: string;
      toDepartmentId?: string;
      toLocationId?: string;
      transferDate: string;
      reason?: string;
    }) => {
      const r = await api.post(`/assets/${assetId}/transfer`, data);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.asset(assetId) });
      qc.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

export function useBulkOperation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      operation: 'DEPRECIATION_RUN' | 'BULK_DISPOSE' | 'BULK_TRANSFER';
      assetIds?: string[];
      payload?: object;
    }) => {
      const r = await api.post('/assets/bulk', data);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets'] });
      qc.invalidateQueries({ queryKey: ['depreciation-runs'] });
      qc.invalidateQueries({ queryKey: QK.dashboard });
    },
  });
}

// ── Categories ────────────────────────────────────────────────────────────────

export function useCategories() {
  return useQuery({
    queryKey: QK.categories,
    queryFn: async () => {
      const r = await api.get<{ data: AssetCategory[] }>('/categories');
      return r.data.data;
    },
  });
}

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<AssetCategory>) => {
      const r = await api.post('/categories', data);
      return r.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.categories }),
  });
}

export function useUpdateCategory(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<AssetCategory>) => {
      const r = await api.put(`/categories/${id}`, data);
      return r.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.categories }),
  });
}

// ── Blocks ────────────────────────────────────────────────────────────────────

export function useBlocks() {
  return useQuery({
    queryKey: QK.blocks,
    queryFn: async () => {
      const r = await api.get<{ data: AssetBlock[] }>('/blocks');
      return r.data.data;
    },
  });
}

export function useCreateBlock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<AssetBlock>) => {
      const r = await api.post('/blocks', data);
      return r.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.blocks }),
  });
}

export function useUpdateBlock(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<AssetBlock>) => {
      const r = await api.put(`/blocks/${id}`, data);
      return r.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.blocks }),
  });
}

// ── Users ─────────────────────────────────────────────────────────────────────

export function useUsers(params: { page?: number; search?: string; role?: string } = {}) {
  return useQuery({
    queryKey: QK.users(params),
    queryFn: async () => {
      const r = await api.get<{ data: User[]; meta: PaginationMeta }>('/users', { params });
      return { items: r.data.data, meta: r.data.meta! };
    },
    placeholderData: keepPreviousData,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      name: string;
      email: string;
      role: string;
      phone?: string;
    }) => {
      const r = await api.post<{ data: User }>('/users', data);
      return r.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useUpdateUser(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<User>) => {
      const r = await api.put<{ data: User }>(`/users/${id}`, data);
      return r.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.user(id) });
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useDeleteUser(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => api.delete(`/users/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

// ── Client Settings ───────────────────────────────────────────────────────────

export function useClientSettings() {
  return useQuery({
    queryKey: QK.settings,
    queryFn: async () => {
      const r = await api.get<{ data: ClientSettings }>('/settings');
      return r.data.data;
    },
  });
}

/**
 * Closed-period lock, in a form the UI can act on.
 *
 * The server is the authority — every guarded route refuses independently of
 * this. The point of surfacing it here is that a user should never be able to
 * fill in a form that is going to be rejected: they should see the field is
 * frozen before they type into it.
 */
export function useBooksLock() {
  const { data: settings, isLoading } = useClientSettings();
  const raw = settings?.booksLockedUpto ?? null;
  const lockDate = raw ? new Date(raw) : null;

  return {
    isLoading,
    lockDate,
    isLocked: lockDate !== null,
    /** dd.mm.yyyy — matches the wording of the server's error messages. */
    lockDateLabel: lockDate
      ? lockDate.toISOString().slice(0, 10).split('-').reverse().join('.')
      : null,
    /**
     * Whether a given asset falls inside the locked window. Mirrors
     * `assetEffectiveDate` on the server: put-to-use, then invoice, then
     * purchase.
     */
    isAssetLocked: (asset?: {
      putToUseDate?: string | null;
      invoiceDate?: string | null;
      purchaseDate?: string | null;
    } | null): boolean => {
      if (!lockDate || !asset) return false;
      const effective = asset.putToUseDate ?? asset.invoiceDate ?? asset.purchaseDate;
      if (!effective) return false;
      return new Date(effective) <= lockDate;
    },
  };
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<ClientSettings>) => {
      const r = await api.put<{ data: ClientSettings }>('/settings', data);
      return r.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.settings }),
  });
}

// ── Financial Years ───────────────────────────────────────────────────────────

export function useFinancialYears() {
  return useQuery({
    queryKey: QK.financialYears,
    queryFn: async () => {
      const r = await api.get<{ data: FinancialYear[] }>('/financial-years');
      return r.data.data;
    },
  });
}

export function useCreateFinancialYear() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { label: string; startDate: string; endDate: string }) => {
      const r = await api.post('/financial-years', data);
      return r.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.financialYears }),
  });
}

export function useCloseFinancialYear(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => api.post(`/financial-years/${id}/close`),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.financialYears }),
  });
}

// ── Branches ──────────────────────────────────────────────────────────────────

export function useBranches() {
  return useQuery({
    queryKey: QK.branches,
    queryFn: async () => {
      const r = await api.get<{ data: Branch[] }>('/branches');
      return r.data.data;
    },
  });
}

// ── Depreciation ──────────────────────────────────────────────────────────────

export function useDepreciationRuns(
  params: object = {},
  options: { refetchInterval?: number | false | ((query: any) => number | false) } = {},
) {
  return useQuery({
    queryKey: QK.depreciationRuns(params),
    queryFn: async () => {
      const r = await api.get<{ data: DepreciationRun[]; meta: PaginationMeta }>(
        '/depreciation/runs', { params }
      );
      return { items: r.data.data, meta: r.data.meta! };
    },
    placeholderData: keepPreviousData,
    refetchInterval: options.refetchInterval,
  });
}

export function useRunDepreciation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      financialYearId: string;
      isYearEnd: boolean;
      notes?: string;
    }) => {
      const r = await api.post('/depreciation/run', data);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['depreciation-runs'] });
      qc.invalidateQueries({ queryKey: ['assets'] });
      qc.invalidateQueries({ queryKey: ['blocks'] });
      qc.invalidateQueries({ queryKey: QK.dashboard });
    },
  });
}

/// Diff between a run and the most recent reversed run for the same FY.
/// Returns added/removed assets so the user can review before posting.
export interface DepreciationRunDiff {
  hasPrevious: boolean;
  previousRunId?: string;
  previousReversedAt?: string | null;
  addedAssets: Array<{
    assetId: string;
    assetCode: string;
    assetName: string;
    caDepreciation: number;
    itDepreciation: number;
  }>;
  removedAssets: Array<{
    assetId: string;
    assetCode: string;
    assetName: string;
    currentStatus: string;
    disposalDate?: string | null;
    wasSoftDeleted: boolean;
    reason: string;
  }>;
  materiallyChanged: unknown[];
}

/// Reverse a posted depreciation run — strict LIFO, requires no later
/// posted run and no disposals against the run's assets after postedAt.
export function useReverseDepreciationRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (runId: string) => {
      const r = await api.post(`/depreciation/runs/${runId}/reverse`);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['depreciation-runs'] });
      qc.invalidateQueries({ queryKey: ['assets'] });
      qc.invalidateQueries({ queryKey: ['blocks'] });
      qc.invalidateQueries({ queryKey: ['client-settings'] });
      qc.invalidateQueries({ queryKey: QK.dashboard });
    },
  });
}

// ── Go-live migration ────────────────────────────────────────────────────────

export interface MigrationStatus {
  isInMigration: boolean;
  goLiveDate: string | null;
  counts: {
    assetCount: number;
    fyCount: number;
    postedRunCount: number;
    bookWDVCount: number;
    openingAdjCount: number;
  };
}

export function useMigrationStatus() {
  return useQuery({
    queryKey: ['migration-status'],
    queryFn: async () => {
      const r = await api.get<{ data: MigrationStatus }>('/migration/status');
      return r.data.data;
    },
    refetchOnWindowFocus: true,
  });
}

export function useStartMigration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { goLiveDate: string }) => {
      const r = await api.post('/migration/start', data);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['migration-status'] });
      qc.invalidateQueries({ queryKey: ['client-settings'] });
    },
  });
}

export interface CreatedFY {
  id: string;
  label: string;
  startDate: string;
  endDate: string;
}

export function useAutoCreateFYs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const r = await api.post<{ data: { created: CreatedFY[]; total: number } }>(
        '/migration/auto-create-fys', {}
      );
      return r.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['migration-status'] });
      qc.invalidateQueries({ queryKey: ['financial-years'] });
    },
  });
}

export function useCompleteMigration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const r = await api.post('/migration/complete');
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['migration-status'] });
    },
  });
}

export interface ReconciliationRow {
  assetId: string;
  assetCode: string;
  name: string;
  category?: string | null;
  block?: string | null;
  cost: number;
  systemCAWDV: number;
  bookCAWDV: number | null;
  diffCA: number | null;
  systemITWDV: number;
  bookITWDV: number | null;
  diffIT: number | null;
  materialityThreshold: number;
  isMaterialCA: boolean;
  bookWDVUploaded: boolean;
  alreadyAdjusted: boolean;
}
export interface ReconciliationTotals {
  assetCount: number;
  uploadedCount: number;
  adjustedCount: number;
  totalSystemCAWDV: number;
  totalBookCAWDV: number;
  totalDiffCA: number;
  totalSystemITWDV: number;
  totalBookITWDV: number;
  totalDiffIT: number;
  materialityPercent: number;
  materialAssetCount: number;
}

export function useReconciliation(enabled: boolean) {
  return useQuery({
    queryKey: ['migration-reconciliation'],
    queryFn: async () => {
      const r = await api.get<{ data: { rows: ReconciliationRow[]; totals: ReconciliationTotals } }>(
        '/migration/reconciliation'
      );
      return r.data.data;
    },
    enabled,
  });
}

export function useNotesToAccounts(enabled: boolean) {
  return useQuery({
    queryKey: ['migration-notes'],
    queryFn: async () => {
      const r = await api.get<{ data: {
        text: string;
        adjustmentCount: number;
        totalDiffCA: number;
        byAccount: Array<{ account: string; total: number; count: number }>;
      } }>('/migration/notes-to-accounts');
      return r.data.data;
    },
    enabled,
  });
}

export function useApplyOpeningAdjustments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { assetIds: string[]; offsetAccount: string; notes?: string }) => {
      const r = await api.post('/migration/apply-adjustments', data);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['migration-reconciliation'] });
      qc.invalidateQueries({ queryKey: ['migration-status'] });
      qc.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

export function useDepreciationRunDiff(runId: string | null) {
  return useQuery({
    queryKey: ['depreciation-run-diff', runId],
    queryFn: async () => {
      const r = await api.get<{ data: DepreciationRunDiff }>(`/depreciation/runs/${runId}/diff`);
      return r.data.data;
    },
    enabled: !!runId,
    staleTime: 0,
  });
}

// ── Disposals ─────────────────────────────────────────────────────────────────

export function useDisposals(params: object = {}) {
  return useQuery({
    queryKey: QK.disposals(params),
    queryFn: async () => {
      const r = await api.get<{ data: AssetDisposal[]; meta: PaginationMeta }>(
        '/disposals', { params }
      );
      return { items: r.data.data, meta: r.data.meta! };
    },
    placeholderData: keepPreviousData,
  });
}

// ── Alerts ────────────────────────────────────────────────────────────────────

export function useAlerts() {
  return useQuery({
    queryKey: QK.alerts,
    queryFn: async () => {
      const r = await api.get<{ data: Alert[] }>('/alerts');
      return r.data.data;
    },
    refetchInterval: 60_000, // Refresh every minute
  });
}

export function useMarkAlertRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (alertId: string) => api.patch(`/alerts/${alertId}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.alerts }),
  });
}

// ── Insurance & AMC ───────────────────────────────────────────────────────────

export function useInsurancePolicies(assetId: string) {
  return useQuery({
    queryKey: QK.insurance(assetId),
    queryFn: async () => {
      const r = await api.get<{ data: InsurancePolicy[] }>(`/assets/${assetId}/insurance`);
      return r.data.data;
    },
    enabled: !!assetId,
  });
}

export function useAMCContracts(assetId: string) {
  return useQuery({
    queryKey: QK.amc(assetId),
    queryFn: async () => {
      const r = await api.get<{ data: AMCContract[] }>(`/assets/${assetId}/amc`);
      return r.data.data;
    },
    enabled: !!assetId,
  });
}

// ── Background Jobs ───────────────────────────────────────────────────────────

export interface BackgroundJob {
  id: string;
  jobType: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'PARTIAL';
  payload: Record<string, any> | null;
  result: Record<string, any> | null;
  completedAt: string | null;
  createdAt: string;
  createdBy: { name: string } | null;
}

export function useBackgroundJobs(params: { page?: number; jobType?: string; status?: string } = {}) {
  // Auto-refresh every 5 seconds when jobs may be in progress
  return useQuery({
    queryKey: ['background-jobs', params],
    queryFn: async () => {
      const r = await api.get<{ data: BackgroundJob[]; meta: PaginationMeta }>('/jobs', { params });
      return { items: r.data.data, meta: r.data.meta! };
    },
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      const items = (query.state.data as any)?.items as BackgroundJob[] | undefined;
      const hasActive = items?.some(j => j.status === 'PENDING' || j.status === 'PROCESSING');
      return hasActive ? 5_000 : false;
    },
  });
}

export function useBackgroundJob(id: string) {
  return useQuery({
    queryKey: ['background-job', id],
    queryFn: async () => {
      const r = await api.get<{ data: BackgroundJob }>(`/jobs/${id}`);
      return r.data.data;
    },
    enabled: !!id,
    refetchInterval: (query) => {
      const job = query.state.data as BackgroundJob | undefined;
      return job?.status === 'PENDING' || job?.status === 'PROCESSING' ? 3_000 : false;
    },
  });
}
